import { TimedMessage } from 'components/TimedMessage';
import { ZButton } from 'components/ZButton';
import { ZMultiSelectDropdown } from 'components/ZMultiSelectDropdown';
import { ZTooltip } from 'components/ZTooltip';
import { PaginationSelectionModel } from 'models/common/paginationSelectionModel';
import { ManagedStockSyncSettings } from 'models/dbModels/syncSettings/ManagedStockSyncSettings';
import { useRef, useState } from 'react';
import { Button, Col, Form, Modal, OverlayTrigger, Row, Tooltip } from 'react-bootstrap';
import { useAppDispatch, useAppSelector } from 'redux/hooks';
import { syncStocks } from 'redux/slices/Stocks';
import { showToast } from 'redux/slices/Toasts';
import { RootState } from 'redux/store';

export function ManagedStockSync(props: any) {
	const form = useRef(null);
	const dispatch = useAppDispatch();
	const [selectedStores, setSelectedStores] = useState<any[] | undefined>(undefined);
	const stores = useAppSelector((state: RootState) => state.stores);
	const global = useAppSelector((state: RootState) => state.global);
	const [options, setOptions] = useState<ManagedStockSyncSettings>({
		name: false,
		supplier: false,
		department: false,
		subDepartment: false,
		manufacturer: false,
		stockGroups: false,
		minOrderQty: false,
		cartonSize: false,
		minStockLevel: false,
		maxStockLevel: false,
		gstCode: false,
		sellPrice: false,
		rrp: false,
		active: false,
		ethical: false,
		locked: false,
		additionalOrderInformation: false,
		apns: false,
		pdes: false,
	})
	const [loading, setLoading] = useState<boolean>(false);
	const selection: PaginationSelectionModel<number> = props.selection;

	function cleanState() {
		setOptions({
			name: false,
			supplier: false,
			department: false,
			subDepartment: false,
			manufacturer: false,
			stockGroups: false,
			minOrderQty: false,
			cartonSize: false,
			minStockLevel: false,
			maxStockLevel: false,
			gstCode: false,
			sellPrice: false,
			rrp: false,
			active: false,
			ethical: false,
			locked: false,
			additionalOrderInformation: false,
			apns: false,
			pdes: false,
		});
	}

	function handleSubmit() {
		//Departments and subdepartments share the values
		setLoading(true);
		const sendOptions: ManagedStockSyncSettings = { ...options, subDepartment: options.department };
		dispatch(syncStocks({
			storeIds: selectedStores?.map(st => st.id) ?? [],
			syncSettings: sendOptions,
			filter: {...props.filter}
		})).then((res) => {
			if (res.data) {
				cleanState();
				props.onClose();
			}

			setLoading(false);
		})
	}

	function handleChange(key: string, value: any) {
		setOptions({ ...options, [key]: value });
	}

	return (
		<>
			<Modal
				size="lg"
				centered
				backdrop={loading ? "static" : true}
				show={props.isOpen}
				onHide={props.onClose}
			>
				<Modal.Header closeButton={!loading}>
					<Modal.Title>
						Manual Sync - {props.counter + " Stock" + (props.counter > 1 ? "s" : "") + " Selected"}
					</Modal.Title>
				</Modal.Header>
				<Modal.Body className={`${loading && "disabled-div"}`}>
					<Row className="mb-1">
						<Col>
							<Form onSubmit={handleSubmit} ref={form}>
								<Row className="mb-1 d-flex align-items-center">
									<Col md={6} className="mb-1">
										<ZMultiSelectDropdown
											hasSearch={true}
											canClear={true}
											label="Stores: "
											values={selectedStores}
											placeholder="Select stores"
											hasAllOption={true}
											options={stores.table}
											display={(s) => s.name}
											value={(s) => s.id}
											minMenuWidthPx={300}
											disabled={(s) => false}
											onSelectionChange={(e) => setSelectedStores(e)} />
									</Col>
									<Col md={6} className="d-flex align-items-end align-self-end mb-1">
										<OverlayTrigger placement="right"
											overlay={
												<Tooltip id="tooltip-right">Always performs a stock match with the selected store(s)</Tooltip>
											}
										>
										<span>
										<Form.Check
											type="checkbox"
											label={
												<>Stock Match 
														<i className="mdi mdi-apple-icloud ms-1"></i> 
												</>}
											className=".custom-control-checkbox-noreverse my-1 me-1 custom-gray-checkbox"
											checked={true}
											onChange={() => { }}
										/>
										</span>
										</OverlayTrigger>
									</Col>
								</Row>
								<Row>
									<Col md={6}>
										<Form.Group>
											<Form.Check
												type="checkbox"
												id="stock-stock-details"
												label={<h6 className="form-title text-dark">Stock Details</h6>}
												className=".custom-control-checkbox-noreverse mb-2 checkboxborder"
												checked={options.name && options.supplier && options.department && options.stockGroups &&
													options.minOrderQty && options.cartonSize && options.minStockLevel && options.maxStockLevel && options.manufacturer}
												onChange={(e) => setOptions({
													...options, name: e.target.checked, supplier: e.target.checked, department: e.target.checked,
													stockGroups: e.target.checked, minOrderQty: e.target.checked,
													cartonSize: e.target.checked, minStockLevel: e.target.checked, maxStockLevel: e.target.checked,
													manufacturer: e.target.checked
												})}
											/>
											<Form.Check
												type="checkbox"
												id="stock-name"
												label="Name"
												className=".custom-control-checkbox-noreverse mb-2"
												checked={options.name}
												onChange={(e) => handleChange('name', e.target.checked)}
											/>
											<Form.Check
												type="checkbox"
												id="stock-supplier"
												label="Supplier"
												className=".custom-control-checkbox-noreverse mb-2"
												checked={options.supplier}
												onChange={(e) => handleChange('supplier', e.target.checked)}
											/>
											<Form.Check
												type="checkbox"
												id="stock-department"
												label="Department/Sub Department"
												className=".custom-control-checkbox-noreverse mb-2"
												checked={options.department}
												onChange={(e) => handleChange('department', e.target.checked)}
											/>
											<Form.Check
												type="checkbox"
												id="stock-manufacturer"
												label="Manufacturer"
												className=".custom-control-checkbox-noreverse mb-2"
												checked={options.manufacturer}
												onChange={(e) => handleChange('manufacturer', e.target.checked)}
											/>
											<Form.Check
												type="checkbox"
												id="stock-stockGroups"
												label="Stock Group"
												className=".custom-control-checkbox-noreverse mb-2"
												checked={options.stockGroups}
												onChange={(e) => handleChange('stockGroups', e.target.checked)}
											/>
											<Form.Check
												type="checkbox"
												id="stock-minOrderQty"
												label="Minimum Order Quantity"
												className=".custom-control-checkbox-noreverse mb-2"
												checked={options.minOrderQty}
												onChange={(e) => handleChange('minOrderQty', e.target.checked)}
											/>
											<Form.Check
												type="checkbox"
												id="stock-cartonSize"
												label="Carton Size"
												className=".custom-control-checkbox-noreverse mb-2"
												checked={options.cartonSize}
												onChange={(e) => handleChange('cartonSize', e.target.checked)}
											/>
											<Form.Check
												type="checkbox"
												id="stock-minStockLevel"
												label="Minimum Stock Level"
												className=".custom-control-checkbox-noreverse mb-2"
												checked={options.minStockLevel}
												onChange={(e) => handleChange('minStockLevel', e.target.checked)}
											/>
											<Form.Check
												type="checkbox"
												id="stock-maxStockLevel"
												label="Maximum Stock Level"
												className=".custom-control-checkbox-noreverse mb-2"
												checked={options.maxStockLevel}
												onChange={(e) => handleChange('maxStockLevel', e.target.checked)}
											/>
										</Form.Group>
									</Col>

									<Col md={6}>
										<Form.Group>
											<Form.Check
												type="checkbox"
												id="stock-pricing-details"
												label={<h6 className="form-title text-dark">Pricing Details</h6>}
												className=".custom-control-checkbox-noreverse mb-2 checkboxborder"
												checked={options.gstCode && options.sellPrice && options.rrp && options.active && options.ethical &&
													options.locked && options.additionalOrderInformation}
												onChange={(e) => setOptions({
													...options, gstCode: e.target.checked, sellPrice: e.target.checked, rrp: e.target.checked,
													active: e.target.checked, ethical: e.target.checked, locked: e.target.checked,
													additionalOrderInformation: e.target.checked
												})}
											/>
											<Form.Check
												type="checkbox"
												id="stock-gstCode"
												label="GST"
												className=".custom-control-checkbox-noreverse mb-2"
												checked={options.gstCode}
												onChange={(e) => handleChange('gstCode', e.target.checked)}
											/>
											<Form.Check
												type="checkbox"
												id="stock-sellPrice"
												label="Sell Price"
												className=".custom-control-checkbox-noreverse mb-2"
												checked={options.sellPrice}
												onChange={(e) => handleChange('sellPrice', e.target.checked)}
											/>
											<Form.Check
												type="checkbox"
												id="stock-rrp"
												label="RRP"
												className=".custom-control-checkbox-noreverse mb-2"
												checked={options.rrp}
												onChange={(e) => handleChange('rrp', e.target.checked)}
											/>
											<Form.Check
												type="checkbox"
												id="stock-active"
												label="Active"
												className=".custom-control-checkbox-noreverse mb-2"
												checked={options.active}
												onChange={(e) => handleChange('active', e.target.checked)}
											/>
											<Form.Check
												type="checkbox"
												id="stock-ethical"
												label="Ethical"
												className=".custom-control-checkbox-noreverse mb-2"
												checked={options.ethical}
												onChange={(e) => handleChange('ethical', e.target.checked)}
											/>
											<Form.Check
												type="checkbox"
												id="stock-locked"
												label="Locked"
												className=".custom-control-checkbox-noreverse mb-2"
												checked={options.locked}
												onChange={(e) => handleChange('locked', e.target.checked)}
											/>
											<Form.Check
												type="checkbox"
												id="stock-additionalOrderInformation"
												label="Additional Order Info"
												className=".custom-control-checkbox-noreverse mb-2"
												checked={options.additionalOrderInformation}
												onChange={(e) => handleChange('additionalOrderInformation', e.target.checked)}
											/>
										</Form.Group>
									</Col>
								</Row>
								<Row className='mt-1'>
									<Col sm={9} className="d-flex align-items-center">
										<b>Please ensure all stock details are finalised before syncing to the selected store(s).</b>
									</Col>
									<Col sm={3} className="d-flex flex-row-reverse">
										<ZTooltip 
											enabled={!selectedStores || selectedStores.length === 0} 
											placement='top' 
											text='Please select at least one store to sync to'
										>
											<ZButton 
												style={{ alignItems: "center" }} 
												variant="primary"
												className="btn ms-1 d-flex cta-button" 
												disabled={loading || !selectedStores || selectedStores.length === 0} 
												onClick={handleSubmit}
												loading={loading}
												buttonText={loading ? "Syncing..." : "Sync"}
											/>
										</ZTooltip>
										<Button
											variant="light"
											className="btn ms-1 cta-button"
											disabled={loading}
											onClick={() => {
												cleanState();
												props.onClose();
											}}
										>Cancel</Button>
										<TimedMessage message="Please wait while we sync all your data..." delay={30} enabled={loading} className="mb-0 py-1 px-2" />
									</Col>
								</Row>
							</Form>
						</Col>
					</Row>
				</Modal.Body>
			</Modal>
		</>
	);
}